import { useAudio } from '@rhg/hooks/use-audio';
import { useMediaQuery, useTheme } from '@rhg/libs/material';

/**
 * Returns layout flags for the AudioPlayerDialog when it is forced into a
 * compact mode (narrow + short viewport + subtitles present). Shared by
 * the dialog (top padding) and its content (description/credits visibility)
 * so both call sites stay in sync.
 *
 * Note: we key off the *presence* of a subtitle track rather than the live
 * CC toggle to avoid the layout snapping when a user enables/disables CC
 * mid-playback.
 */
export function useCompactAudioDialogLayout(hasDialogBottomAction: boolean) {
    const theme = useTheme();
    const { audio } = useAudio();

    /**
     * Extra vertical space the dialog gives up when a bottom action button
     * (e.g. "Open Worksheet") is rendered. Used to inflate the height
     * thresholds below so they compare against the *effective* space
     * available to the player's main column.
     *
     * TODO(IB-918): replace this approximation with the exact value once
     * https://calmdotcom.atlassian.net/browse/IB-918 lands and the bottom
     * action area's measured height is finalised.
     */
    const BOTTOM_ACTION_HEIGHT_PX = 22 * theme.semantics.Spacing[4];

    const hasSubtitleTrack = !!audio?.subtitleSrc;

    const heightOffset = hasDialogBottomAction ? BOTTOM_ACTION_HEIGHT_PX : 0;
    const isShortFor600 = useMediaQuery(`(max-height:${600 + heightOffset}px)`);
    const isShortFor750 = useMediaQuery(`(max-height:${750 + heightOffset}px)`);
    const isShortFor820 = useMediaQuery(`(max-height:${820 + heightOffset}px)`);
    const isNarrow600 = useMediaQuery('(max-width:600px)');

    const isCompact =
        hasSubtitleTrack && (isShortFor750 || (isNarrow600 && isShortFor820));

    return {
        shouldHideExtras: isCompact,
        // Not gated on hasSubtitleTrack: padding reduction fires whenever
        // vertical space is tight, independent of CC presence. Only
        // shouldHideExtras gates on subtitles, because the description and
        // credits only need to yield space when subtitles are competing for
        // it. At 600-750px the shouldHideExtras path already reclaims room
        // for subtitle sessions, and non-subtitle sessions have enough room
        // at those heights without trimming the padding.
        shouldReduceTopPadding: isShortFor600,
    };
}
